import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sticky"]

  connect() {
    this.bindKeyboardWatcher()
  }

  disconnect() {
    if (this.viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.viewportHandler)
    }
  }

  // Submit the closest form via requestSubmit() so Turbo intercepts it as a Stream request.
  // The app runs with Turbo Drive disabled globally, but requestSubmit() still goes through
  // Turbo when the form carries data-turbo="true" — a plain .submit() would not.
  submitForm(event) {
    const form = event.target.closest("form")
    if (!form) return

    form.requestSubmit()
  }

  // Sticky bar CTA: the payment form is further down the same page, so the button scrolls to
  // it rather than navigating. Classic JS only (old Babel — no ?./??).
  // The mockup shows ONE CTA per viewport: the recap's on desktop (.deskonly), this sticky
  // bar's below 880px. The payment form's own submit is hidden on mobile to match, so this
  // bar is the only way to pay there — it must submit, not merely scroll. Scrolling alone
  // broke precisely where it mattered: with the page already at the bottom, scrollIntoView
  // had nowhere to go and the button did nothing.
  //
  // Ported from KubeAstro::astro_cart_controller#scrollToPayment, which solved this first;
  // every guard below was paid for there.
  scrollToPayment(event) {
    event.preventDefault()

    const frame = document.getElementById("cosy-payment-step")
    if (!frame) return

    // A submit in flight disables the button. Don't fire a second one — that is a double
    // charge. cosy-payment#submit re-enables it on error, so retries still work.
    const mountedButton = frame.querySelector("[data-cosy-payment-target='submitButton']")
    if (mountedButton && mountedButton.disabled) return

    // Only scroll when the form isn't already in view: once the customer is looking at it,
    // re-scrolling on submit is an unwanted jump.
    if (!this.isInViewport(frame)) {
      frame.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    if (mountedButton) {
      this.submitPaymentWhenReady(frame)
    } else {
      // Frame still loading (or just rebuilt after a cart edit): submit once it lands.
      const onLoad = (event) => {
        if (event.target.id !== "cosy-payment-step") return
        document.removeEventListener("turbo:frame-load", onLoad)
        this.submitPaymentWhenReady(frame)
      }
      document.addEventListener("turbo:frame-load", onLoad)
    }
  }

  // True when ANY part of the frame overlaps the viewport, so a tall form the customer has
  // scrolled INTO still counts as in view and the page is not yanked back up on submit.
  isInViewport(frame) {
    const rect = frame.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    return rect.top < viewportHeight && rect.bottom > 0
  }

  // Stripe mounts asynchronously. Poll briefly for the submit button, then click it (its
  // data-action runs cosy-payment#submit). Give up after ~5s so a stuck frame can't hang.
  submitPaymentWhenReady(frame, attempts = 0) {
    const button = frame.querySelector("[data-cosy-payment-target='submitButton']")
    if (button) {
      if (!button.disabled) button.click()
      return
    }
    if (attempts >= 50) return
    setTimeout(() => this.submitPaymentWhenReady(frame, attempts + 1), 100)
  }

  // Hide the sticky total while the on-screen keyboard is up: on mobile it otherwise covers
  // the field being typed into. visualViewport shrinks when the keyboard opens.
  bindKeyboardWatcher() {
    if (!window.visualViewport || !this.hasStickyTarget) return

    const fullHeight = window.innerHeight
    this.viewportHandler = () => {
      const shrunk = window.visualViewport.height < fullHeight * 0.75
      this.stickyTarget.classList.toggle("is-hidden", shrunk)
    }
    window.visualViewport.addEventListener("resize", this.viewportHandler)
  }
}
